/**
 * The site header's link structure, read out of the published navigation markup
 * on https://www.webshare.io/blog.
 *
 * This is editorial data. Nothing derives it, not the CMS, not the router, not
 * the design, so it gets the same treatment as `BlogListingPage`'s category
 * filter order: one hand-kept list, in the published order, and the only place
 * a menu's wording or destination is decided.
 *
 * Every `?source=` key is verbatim: that is what the site's attribution reads,
 * and rewriting one would change what it records. The paths are not: the
 * published menu mixes absolute and root-relative forms because it is served
 * from the marketing origin, and a root-relative marketing path resolves
 * against THIS origin and 404s. Each one therefore names its host, and only the
 * destinations this app actually serves stay relative.
 */

/** The tinted square an item's icon sits in. */
type IconTone = "violet" | "amber" | "mint" | "plain";

export type FeatureLink = {
  label: string;
  summary: string;
  /**
   * A country's code, on a Locations row: the summary is then its unit, and the
   * row prints that country's IP count before it (`headerCounts.tsx`).
   */
  countOf?: string;
  href: string;
  icon: string;
  tone: IconTone;
  /** The right-hand price block; absent on the menus that quote no price. */
  price?: { caption: string; value: string };
};

export type CompactLink = {
  label: string;
  href: string;
  icon: string;
  price: string;
  tone: "violet" | "mint";
};

export type PlainLink = {
  label: string;
  href: string;
  icon: string;
  /** The tag at the end of the row, for an entry new enough to point out. */
  badge?: string;
};

export type NavBlock =
  | { kind: "features"; items: FeatureLink[] }
  | { kind: "compact"; items: CompactLink[] }
  | { kind: "plain"; items: PlainLink[] };

/** A titled list of plain rows. */
type NavList = { heading: string; items: PlainLink[] };

export type NavColumn = {
  heading?: string;
  blocks: NavBlock[];
  /** A second list, beside the column's own wherever both fit and under it elsewhere. */
  beside?: NavList;
};

export type NavFooter =
  | { kind: "banner"; label: string; summary: string; href: string }
  | { kind: "button"; label: string; href: string };

export type NavMenu = {
  id: string;
  label: string;
  columns: NavColumn[];
  footer?: NavFooter;
};

const ICONS = "/marketing/nav";

/** Root-relative, not the published host: see `sitePath` in `../marketingLinks`. */
const SITE = "";

const PRODUCTS: NavMenu = {
  id: "products",
  label: "Products",
  columns: [
    {
      heading: "Residential",
      blocks: [
        {
          kind: "features",
          items: [
            {
              label: "Rotating Residential Proxy",
              summary: "80m+ Residential IPs, pay only for bandwidth",
              href: `${SITE}/residential-proxy?source=nav_bar`,
              icon: `${ICONS}/residential-proxy.svg`,
              tone: "violet",
              price: { caption: "Starting at", value: "$1.4/GB" },
            },
            {
              label: "Static Residential Proxy",
              summary: "Premium proxies with static Residential IP",
              href: `${SITE}/static-residential-proxy?source=nav_bar`,
              icon: `${ICONS}/static-residential-proxy.svg`,
              tone: "violet",
              price: { caption: "Starting at", value: "$0.23/IP" },
            },
          ],
        },
        {
          kind: "compact",
          items: [
            {
              label: "Private Static Residential",
              href: `${SITE}/private-static-residential-proxy`,
              icon: `${ICONS}/private-static-residential.svg`,
              price: "$0.53/IP",
              tone: "violet",
            },
            {
              label: "Dedicated Static Residential",
              href: `${SITE}/dedicated-static-residential-proxy`,
              icon: `${ICONS}/dedicated-static-residential.svg`,
              price: "$1.47/IP",
              tone: "violet",
            },
          ],
        },
      ],
    },
    {
      heading: "Datacenter",
      blocks: [
        {
          kind: "features",
          items: [
            {
              label: "Free Proxy",
              summary: "Free proxy list up to 1GB/mo bandwidth",
              href: `${SITE}/features/free-proxy`,
              icon: `${ICONS}/free-proxy.svg`,
              tone: "amber",
              price: { caption: "Get it for", value: "FREE" },
            },
            {
              label: "Proxy Servers",
              summary: "Fast & affordable proxy server list",
              href: `${SITE}/proxy-server?source=nav_bar`,
              icon: `${ICONS}/proxy-servers.svg`,
              tone: "mint",
              price: { caption: "Starting at", value: "$0.018/IP" },
            },
          ],
        },
        {
          kind: "compact",
          items: [
            {
              label: "Private Proxy Servers",
              href: `${SITE}/private-datacenter-proxy`,
              icon: `${ICONS}/private-proxy-servers.svg`,
              price: "$0.43/IP",
              tone: "mint",
            },
            {
              label: "Dedicated Proxy Servers",
              href: `${SITE}/dedicated-datacenter-proxy`,
              icon: `${ICONS}/dedicated-proxy-servers.svg`,
              price: "$0.77/IP",
              tone: "mint",
            },
          ],
        },
      ],
    },
    {
      heading: "Specialty",
      blocks: [
        {
          kind: "plain",
          items: [
            {
              label: "YouTube Proxies",
              href: `${SITE}/youtube-proxy`,
              icon: `${ICONS}/youtube-proxies.svg`,
            },
            {
              label: "Socks5 Proxy",
              href: `${SITE}/features/socks5-proxy`,
              icon: `${ICONS}/socks5-proxy.svg`,
            },
            {
              label: "HTTP Proxy",
              href: `${SITE}/features/http-proxy`,
              icon: `${ICONS}/http-proxy.svg`,
            },
          ],
        },
      ],
      beside: {
        heading: "Tools and add-ons",
        items: [
          {
            label: "Proxy extension",
            href: `${SITE}/chrome-proxy-extension`,
            icon: `${ICONS}/proxy-extension.svg`,
          },
          // In Dashboard's place since the public tester (R-298).
          {
            label: "Proxy Tester",
            href: `${SITE}/proxy-checker`,
            icon: `${ICONS}/proxy-tester.svg`,
          },
          // Not on the published menu: added with the /mcp-server page (R-2186).
          {
            label: "Webshare MCP",
            href: `${SITE}/mcp-server`,
            icon: `${ICONS}/webshare-mcp.svg`,
            badge: "NEW",
          },
        ],
      },
    },
  ],
  footer: {
    kind: "banner",
    label: "Compare Products",
    summary: "Find the Best Plan for Your Needs",
    href: `${SITE}/product-comparison?source=nav_bar`,
  },
};

/** Two per column, so the pairs below are the published columns. */
function pairs(items: FeatureLink[]): NavColumn[] {
  const columns: NavColumn[] = [];
  for (let index = 0; index < items.length; index += 2) {
    columns.push({
      blocks: [{ kind: "features", items: items.slice(index, index + 2) }],
    });
  }
  return columns;
}

const USE_CASES: NavMenu = {
  id: "use-cases",
  label: "Use Cases",
  columns: pairs([
    {
      label: "SEO",
      summary: "Scrape SERPs and public search data at scale.",
      href: `${SITE}/use-cases/seo-proxies`,
      icon: `${ICONS}/seo.svg`,
      tone: "plain",
    },
    {
      label: "Social Media Proxies",
      summary: "Reliable proxies for social media management.",
      href: `${SITE}/use-cases/social-media-proxies`,
      icon: `${ICONS}/social-media.svg`,
      tone: "plain",
    },
    {
      label: "Web Scraping Proxy",
      summary: "Extract public data without bans or rate limits.",
      href: `${SITE}/use-cases/web-scraping-data-extraction`,
      icon: `${ICONS}/web-scraping.svg`,
      tone: "plain",
    },
    {
      label: "Proxies for Ad Verification",
      summary: "Verify and monitor ads across locations.",
      href: `${SITE}/use-cases/proxies-for-ad-verification-monitoring`,
      icon: `${ICONS}/ad-verification.svg`,
      tone: "plain",
    },
    {
      label: "Gaming Proxy",
      summary: "Reduce latency and bypass geo-restrictions.",
      href: `${SITE}/use-cases/gaming-proxy-server`,
      icon: `${ICONS}/gaming.svg`,
      tone: "plain",
    },
    {
      label: "Proxies For Market Research",
      summary: "Run research without IP or geo restrictions.",
      href: `${SITE}/use-cases/proxies-for-market-research`,
      icon: `${ICONS}/market-research.svg`,
      tone: "plain",
    },
  ]),
  footer: {
    kind: "button",
    label: "View All Use Cases",
    href: `${SITE}/use-cases`,
  },
};

/**
 * The countries the Locations menu names, in its order. Each row prints the
 * country's IP count as the CMS holds it (`proxyLocation`), as every page does,
 * read while the page renders (`headerCounts.tsx`); the published menu shipped
 * them as literal text, retyped whenever they changed.
 */
const LOCATION_ROWS = [
  { label: "United States", code: "us" },
  { label: "France", code: "fr" },
  { label: "United Kingdom", code: "gb" },
  { label: "Japan", code: "jp" },
  { label: "Germany", code: "de" },
  { label: "Australia", code: "au" },
  { label: "Canada", code: "ca" },
  { label: "China", code: "cn" },
];

/** The codes whose counts the header prints, which it hands to the browser. */
export const LOCATION_MENU_CODES = LOCATION_ROWS.map(({ code }) => code);

const LOCATIONS: NavMenu = {
  id: "locations",
  label: "Locations",
  columns: pairs(
    LOCATION_ROWS.map(({ label, code }) => ({
      label,
      summary: "IPs",
      countOf: code,
      href: `/proxy-locations/${code}`,
      icon: `${ICONS}/flag-${code}.svg`,
      tone: "plain" as const,
    }))
  ),
  footer: {
    kind: "button",
    label: "See All Locations",
    href: `${SITE}/features/proxy-locations`,
  },
};

const RESOURCES: NavMenu = {
  id: "resources",
  label: "Resources",
  columns: pairs([
    {
      label: "Blog",
      summary: "Proxy guides, tutorials, and product updates",
      href: "/blog",
      icon: `${ICONS}/blog.svg`,
      tone: "plain",
    },
    {
      label: "Academy",
      summary: "Learn scraping with JavaScript and Python",
      href: "/academy/scraping",
      icon: `${ICONS}/academy.svg`,
      tone: "plain",
    },
    {
      label: "Get Started",
      summary: "Quick start guides",
      href: "https://help.webshare.io/en/collections/6097897-getting-started",
      icon: `${ICONS}/get-started.svg`,
      tone: "plain",
    },
    {
      label: "Integrations",
      summary: "Proxy with your favourite applications",
      href: `${SITE}/integrations`,
      icon: `${ICONS}/integrations.svg`,
      tone: "plain",
    },
    {
      label: "Docs",
      summary: "Find Answers, Tips",
      href: "https://help.webshare.io/en/",
      icon: `${ICONS}/docs.svg`,
      tone: "plain",
    },
    {
      label: "API Docs",
      summary: "Access to restful proxy APIs",
      href: "https://apidocs.webshare.io/",
      icon: `${ICONS}/api-docs.svg`,
      tone: "plain",
    },
  ]),
};

export const NAV_MENUS = [PRODUCTS, USE_CASES, LOCATIONS, RESOURCES];

/** The one top-level item with no menu behind it. */
export const PRICING_LINK = { label: "Pricing", href: "/pricing" };

/**
 * The wordmark returns to the marketing home, as the published one does: the
 * root, which on the marketing host is that page (`src/routing/marketingHome.ts`).
 *
 * On the dashboard host the root is still the app shell, so a reader of one of
 * the marketing copies served there is sent into the dashboard by this link.
 * Those copies are the staging and preview view until the domain moves, and
 * are redirected to the marketing host after it, so no page keeps the old
 * `/home` link and has to hop through the redirect that address answers.
 */
export const HOME_HREF = `${SITE}/`;

/** Destinations in the header this app serves itself, as typed client routes. */
export const BLOG_HREF = "/blog";

/**
 * Both sign-in doors resolve in this app rather than on `dashboard.webshare.io`:
 * the routes are `_app/login` and `_app/register` here. The `?source=` keys are
 * unchanged: the site's attribution reads them.
 */
export const LOG_IN_LINK = {
  label: "Log In",
  href: "/login?source=nav_login",
};

export const SIGN_UP_LINK = {
  label: "Sign Up",
  href: "/register?source=nav_register",
};

export const BOOK_CALL_LABEL = "Book a Call";
